import { useCallback, useMemo, useState } from "react";
import { SpinnerCircular } from "spinners-react";
import { useGetFieldLabel } from "../../contexts/LanguageContext";
import {
  TApplicationField,
  TApplicationFieldOption,
} from "../../hooks/application-fields.hooks";
import { useFilterFields } from "../../hooks/applications.hooks";
import { useMediaQuery } from "../../hooks/common";
import { shorten } from "../../utils/common";
import { InputProps } from "../CommonComponents/Input/Input";

export type ApplicationFilterState = {
  connector: "and" | "or";
  conditions: {
    id: string;
    field: string;
    connector: string;
    value: string;
  }[];
};

export default function ApplicationsFilter({
  defaultFilter,
  onChange,
  onClose,
}: {
  onChange: (_: ApplicationFilterState) => unknown;
  defaultFilter: ApplicationFilterState;
  onClose: () => unknown;
}) {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const { isFetching, isError, data: fields } = useFilterFields();
  const convert = useGetFieldLabel();
  const [state, setState] = useState<ApplicationFilterState>(
    defaultFilter?.conditions?.length
      ? defaultFilter
      : {
          connector: "and",
          conditions: [
            { connector: "", field: "", id: Date.now().toString(), value: "" },
          ],
        }
  );

  const getOptions = useCallback((field: TApplicationField) => {
    switch (field.type) {
      case "integer":
      case "area":
      case "float":
        return [
          { value: "eq", label: "Equals" },
          { value: "notEq", label: "Not Equals" },
          { value: "gte", label: "Greater Than Or Equal To" },
          { value: "lte", label: "Less Than Or Equal To" },
        ];
      case "boolean":
      case "select":
      case "configurable-select": {
        return [
          { value: "eq", label: "Equals" },
          { value: "notEq", label: "Not Equals" },
        ];
      }
      case "date":
        return [
          { value: "onOrAfter", label: "On Or After" },
          { value: "onOrBefore", label: "On Or Before" },
        ];
      default:
        return [
          { value: "eq", label: "Equals" },
          { value: "notEq", label: "Not Equals" },
          { value: "like", label: "Contains" },
          { value: "notLike", label: "Not Contains" },
          { value: "begins", label: "Begins With" },
          { value: "notBegins", label: "Does Not Begin With" },
          { value: "ends", label: "Ends With" },
          { value: "notEnds", label: "Does Not End With" },
        ];
    }
  }, []);

  const fieldMap = useMemo(
    () =>
      (fields || []).reduce<Map<string, TApplicationField>>(
        (acc, e) => acc.set(e.uid, e),
        new Map()
      ),
    [fields]
  );

  if (isFetching || isError || !fields?.length) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError || (!isFetching && !fields?.length) ? (
          <p>Something went wrong while fetching filter info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  const addCondition = () =>
    setState((e) => ({
      ...e,
      conditions: [
        ...e.conditions,
        { connector: "", field: "", id: Date.now().toString(), value: "" },
      ],
    }));

  const onFieldChange = (i: number, value: string) => {
    setState((e) => ({
      ...e,
      conditions: e.conditions.map((o, idx) =>
        idx === i
          ? {
              id: Date.now().toString(),
              field: value,
              connector: "",
              value: "",
            }
          : o
      ),
    }));
  };

  const onConnectorChange = (i: number, value: string) => {
    setState((e) => ({
      ...e,
      conditions: e.conditions.map((o, idx) =>
        idx === i ? { ...o, connector: value } : o
      ),
    }));
  };

  const onValueChange = (i: number, value: string) => {
    setState((e) => ({
      ...e,
      conditions: e.conditions.map((o, idx) =>
        idx === i ? { ...o, value } : o
      ),
    }));
  };

  const onDeleteCondition = (i: number) => {
    setState((e) => ({
      ...e,
      conditions: e.conditions.filter((_, idx) => idx !== i),
    }));
  };

  const shouldRender = (i: number) => isMobile || !i;

  return (
    <>
      <div className="flex justify-between items-center shrink-0 mb-3 p-6">
        <h4 className="m-0 text-2xl font-bold">Filter</h4>
        <button className="text-xl" onClick={onClose}>
          <i className="fa fa-close m-0"></i>
        </button>
      </div>
      <div className="px-6 pb-6 overflow-auto">
        <div className="mb-4">
          <select
            value={state.connector}
            className="p-2 border rounded-xl"
            onChange={(e) =>
              setState((prev) => ({
                ...prev,
                connector: e.target.value === "and" ? "and" : "or",
              }))
            }
          >
            <option value="and">AND</option>
            <option value="or">OR</option>
          </select>
        </div>
        {state.conditions.map((e, i) => (
          <div
            key={e.id}
            className="flex justify-between items-center w-full flex-wrap md:flex-nowrap my-2"
          >
            <div className="flex justify-between grow flex-wrap items-center">
              <div className="basis-full md:basis-1/3 shrink-0 md:pr-2 md:py-2 ">
                {shouldRender(i) && (
                  <p className="mb-1 pl-1 mt-2 md:mt-0">Field</p>
                )}
                <select
                  className="w-full p-2 border rounded-xl"
                  value={e.field}
                  onChange={(e) => onFieldChange(i, e.target.value)}
                >
                  <option hidden>Select</option>
                  {(fields || []).map((o) => (
                    <option
                      key={`${o.uid}-${e.id}`}
                      value={o.uid}
                      title={convert(o)}
                    >
                      {shorten(convert(o))}
                    </option>
                  ))}
                </select>
              </div>
              <div className="basis-full md:basis-1/3 shrink-0 md:px-1 md:py-2">
                {shouldRender(i) && (
                  <p className="mb-1 pl-1 mt-2 md:mt-0">Operator</p>
                )}
                <select
                  disabled={!e.field}
                  className="w-full p-2 border rounded-xl"
                  value={e.connector}
                  onChange={(e) => onConnectorChange(i, e.target.value)}
                >
                  <option hidden>Select</option>
                  {e?.field && fieldMap.get(e.field)
                    ? getOptions(fieldMap.get(e.field)!).map((o) => (
                        <option value={o.value} key={`${o.value}-${e.id}`}>
                          {o.label}
                        </option>
                      ))
                    : null}
                </select>
              </div>
              <div className="basis-full md:basis-1/3 shrink-0 md:pl-2 md:py-2">
                {shouldRender(i) && (
                  <p className="mb-1 pl-1 mt-2 md:mt-0">Value</p>
                )}
                {e?.field && fieldMap.get(e.field) ? (
                  <RenderField
                    field={fieldMap.get(e.field)!}
                    value={e.value}
                    onChange={(val) => onValueChange(i, val)}
                    className="border rounded-xl"
                  />
                ) : (
                  <select disabled className="w-full p-2 border rounded-xl">
                    <option hidden>Select</option>
                  </select>
                )}
              </div>
            </div>
            <button
              className="p-1 px-2 ml-2"
              style={{ ...(!i && { opacity: 0, pointerEvents: "none" }) }}
            >
              <i
                className="text-xl text-red-600 fa fa-trash m-0"
                onClick={() => onDeleteCondition(i)}
              ></i>
            </button>
          </div>
        ))}

        <button
          className="px-4 bg-gray-200 py-1 my-1 rounded"
          onClick={addCondition}
        >
          Add
        </button>
      </div>
      <div className="flex justify-center mb-6 items-center space-x-4">
        <button
          onClick={() => {
            onChange({
              ...state,
              conditions: state.conditions.filter(
                (e) => e.connector && e.field && e.value
              ),
            });
            onClose();
          }}
          type="button"
          className="bg-primary px-6 py-2 rounded-full font-bold text-white"
        >
          Apply
        </button>
        <button
          onClick={() => {
            setState({
              connector: "and",
              conditions: [
                {
                  connector: "",
                  field: "",
                  id: Date.now().toString(),
                  value: "",
                },
              ],
            });
            onChange({ conditions: [], connector: "and" });
          }}
          type="button"
          className="bg-black px-6 py-2 rounded-full font-bold text-white"
        >
          Clear
        </button>
      </div>
    </>
  );
}

type PropTypes = {
  value: string;
  className?: string;
  onChange: (x: string) => unknown;
  field: TApplicationField & {
    options?: TApplicationFieldOption[];
  };
};

function RenderInput({ field, value, onChange, className }: PropTypes) {
  let props: InputProps = {};

  switch (field.type) {
    case "integer":
    case "area":
    case "float": {
      props = { type: "number" };
      break;
    }
    case "date": {
      props = { type: "date" };
      break;
    }
  }
  return (
    <input
      {...props}
      className={`w-full p-1.5 bg-[rgb(239,_239,_239)] ${className ?? ""}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function RenderSelect({ field, value, onChange, className }: PropTypes) {
  let options: { id: number | boolean; name: string; active?: boolean }[] =
    field.type === "boolean"
      ? [
          { id: true, name: "Yes", active: true },
          { id: false, name: "No", active: true },
        ]
      : field?.options || [];

  return (
    <select
      className={`w-full p-2 ${className ?? ""}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option hidden></option>
      {options
        ?.filter((e) => e.active)
        ?.map((e) => (
          <option key={`${field.id}-${e.id}`} value={String(e.id)}>
            {e.name}
          </option>
        ))}
    </select>
  );
}

function RenderField({ field, ...props }: PropTypes) {
  switch (field.type) {
    case "boolean":
    case "configurable-select":
    case "select":
      return <RenderSelect field={field} {...props} />;
    case "integer":
    case "float":
    case "area":
    case "date":
    default:
      return <RenderInput field={field} {...props} />;
  }
}
